import { useRefHistory } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { useWidgetContext } from "@/shared/config";
import { startViewTransition } from "@/shared/ui";

import type { WidgetStep } from "./steps";

import { FIRST_STEP } from "./steps";

export const useGlobalStore = defineStore("global", () => {
    const { settings } = useWidgetContext();

    const currentStep = ref<WidgetStep>(FIRST_STEP);
    // sync: every step change is recorded, even several within one tick
    const stepHistory = useRefHistory(currentStep, { flush: "sync" });

    const isStarted = ref(false);

    const isPopupOpened = ref(false);

    const modeIsEmbed = computed(() => settings.mode === "embed");
    const modeIsPopup = computed(() => settings.mode === "popup");

    /** Встроенный виджет всегда открыт, popup - пока его не закрыли */
    const isOpen = computed(() => modeIsEmbed.value || isPopupOpened.value);

    const isFirstStep = computed(() => currentStep.value === FIRST_STEP);

    function isCurrentStep(step: WidgetStep): boolean {
        return currentStep.value === step;
    }

    function setStep(toStep: WidgetStep): void {
        if (toStep === currentStep.value) return;

        currentStep.value = toStep;
    }

    /** Переход на шаг с анимацией через View Transitions API */
    async function switchStep(toStep: WidgetStep): Promise<void> {
        if (toStep === currentStep.value) return;

        await startViewTransition(() => setStep(toStep));
    }

    /** Возврат на шаг, с которого пришли. Без истории - на первый шаг */
    async function backToPrevStep(): Promise<void> {
        if (!stepHistory.canUndo.value) return switchStep(FIRST_STEP);

        await startViewTransition(() => stepHistory.undo());
    }

    /**
     * Сброс на первый шаг без своей анимации: виджет в этот момент скрыт или только открывается,
     * либо сброс уже идёт внутри startViewTransition вместе с другими изменениями
     */
    function resetStep(): void {
        setStep(FIRST_STEP);
        // The first step starts a new route, there is nothing to go back to
        stepHistory.clear();
    }

    function openWidget(): void {
        isPopupOpened.value = true;
    }

    function closeWidget(): void {
        isPopupOpened.value = false;
        resetStep();
    }

    return {
        currentStep,
        isStarted,
        isPopupOpened,

        modeIsEmbed,
        modeIsPopup,
        isOpen,
        isFirstStep,

        isCurrentStep,
        switchStep,
        backToPrevStep,
        resetStep,
        openWidget,
        closeWidget,
    };
});
